// DB-Frontend/src/pages/FormPage.js
import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import logo from "../../Assets/images/logo.png";
import Header from "../Header/header";
import Footer from "../Footer/footer";
import { registerUser, getFields } from "./_request";

// Initial mapping: match current field titles to udf1, udf2, ...
const initialFieldTitles = [
  "Full Name",
  "Age",
  "City",
  "Country",
  "Class Level",
  "Class Name",
  "Teacher Name",
  "Class Location (area)",
  "Father/Guardian Name",
  "Father's Contact (optional)",
  "Contact Number",
  "Job (optional)",
  "Studies",
  "Skill Set",
  "Marital Status",
];

function FormPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { approved } = location.state || {};
  const [fields, setFields] = useState([]); // [{ key: 'udf1', label: 'Full Name' }, ...]
  const [formData, setFormData] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!approved) {
      alert("You are not authorized to access this page.");
      navigate("/database-enterKey");
    }
  }, [approved, navigate]);

  useEffect(() => {
    async function fetchFields() {
      setLoading(true);
      try {
        const res = await getFields();
        const f = res.fields || {};
        // Build array of { key, label } for labeled fields only
        const mapped = [];
        for (let i = 1; i <= 50; i++) {
          const label = f[`udf${i}_label`];
          if (label && label.trim() !== "") {
            mapped.push({ key: `udf${i}`, label });
          }
        }
        // If no labels set yet, use initial mapping for first N fields
        if (mapped.length === 0) {
          for (let i = 0; i < initialFieldTitles.length; i++) {
            mapped.push({ key: `udf${i + 1}`, label: initialFieldTitles[i] });
          }
        }
        setFields(mapped);
        // Initialize form state for only labeled fields
        const initial = {};
        mapped.forEach((f) => {
          initial[f.key] = "";
        });
        setFormData(initial);
      } catch (err) {
        alert("Failed to load form fields.");
      } finally {
        setLoading(false);
      }
    }
    fetchFields();
  }, []);

  const handleChange = (e, key) => {
    setFormData({ ...formData, [key]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    // Validate required fields (all mapped fields are required except those with 'optional' in label)
    for (const f of fields) {
      if (
        !/optional/i.test(f.label) &&
        (!formData[f.key] || formData[f.key].trim() === "")
      ) {
        alert(`Please fill the required field: ${f.label}`);
        return;
      }
    }
    try {
      const submission = {};
      fields.forEach((f) => {
        if (formData[f.key] && formData[f.key].trim() !== "") {
          submission[f.key] = formData[f.key];
        }
      });
      const response = await registerUser(submission);
      if (response.success) {
        alert("Form submitted successfully!");
        navigate("/database-thankyou");
      }
    } catch (err) {
      console.error("Form submission error:", err);
      alert("Failed to submit. Please try again.");
    }
  };

  return (
    <>
      <Header />
      <div className="flex flex-col items-center justify-center min-h-screen py-12 px-4 sm:px-6 lg:px-8">
        <div className="w-full max-w-4xl space-y-8">
          <div className="text-center">
            <img src={logo} alt="AMN Logo" className="w-20 h-20 mx-auto" />
            <h1 className="text-4xl font-bold mt-4">Register Yourself</h1>
          </div>

          {loading ? (
            <div className="text-center py-10 text-lg text-gray-500">
              Loading form...
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 px-4">
                {fields.map((f, idx) => (
                  <div key={f.key} className="flex flex-col">
                    <label className="block font-medium">{f.label}</label>
                    <input
                      name={f.key}
                      value={formData[f.key] || ""}
                      onChange={(e) => handleChange(e, f.key)}
                      required={!/optional/i.test(f.label)}
                      className="w-full border p-2 rounded"
                    />
                  </div>
                ))}
              </div>
              <div className="text-center">
                <button
                  type="submit"
                  className="bg-primary hover:bg-blue-400 text-white font-semibold py-2 px-10 rounded transition duration-200"
                >
                  Submit
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
      <Footer />
    </>
  );
}

export default FormPage;
